import { ref, computed } from 'vue'

class LoadingOrchestrator {
  constructor() {
    this.isInitialized = ref(false)
    this.assetsLoaded = ref(false)
    this.criticalResourcesLoaded = ref(false)
    this.loadingProgress = ref(0)
    this.loadingComplete = ref(false)
    this.pageEntranceReady = ref(false)
    
    this.criticalAssets = [
      '/images/campaign.webp'
    ]
    
    this.audioAssets = []

    this.loadedAssets = new Set()
    this.totalAssets = this.criticalAssets.length + this.audioAssets.length
    
    this.callbacks = {
      onProgress: [],
      onCriticalLoaded: [],
      onAllLoaded: [],
      onReadyForEntrance: []
    }
  }

  init() {
    if (this.isInitialized.value) return Promise.resolve()
    
    this.isInitialized.value = true
    
    return new Promise((resolve) => {
      // For LCP optimization: Skip critical asset preloading, 
      // they're already preloaded via HTML link tags
      this.criticalResourcesLoaded.value = true
      this.triggerCallbacks('onCriticalLoaded')
      
      
      resolve()
    })
  }

  preloadCriticalAssets() {
    const promises = this.criticalAssets.map(src => this.preloadImage(src))
    
    return Promise.allSettled(promises).then(results => {
      const successful = results.filter(result => result.status === 'fulfilled').length
      console.log(`Critical assets loaded: ${successful}/${this.criticalAssets.length}`)
    })
  }


  preloadImage(src) {
    return new Promise((resolve, reject) => {
      const img = new Image()
      
      img.onload = () => {
        this.loadedAssets.add(src)
        this.updateProgress()
        resolve(src)
      }
      
      img.onerror = () => {
        console.warn(`Failed to preload image: ${src}`)
        reject(new Error(`Image load failed: ${src}`))
      }
      
      img.src = src
    })
  }


  updateProgress() {
    const progress = (this.loadedAssets.size / this.totalAssets) * 100
    this.loadingProgress.value = progress
    this.triggerCallbacks('onProgress', progress)
  }

  checkAllAssetsLoaded() {
    if (this.criticalResourcesLoaded.value) {
      this.assetsLoaded.value = true
      this.triggerCallbacks('onAllLoaded')
    }
  }

  markLoadingComplete() {
    this.loadingComplete.value = true
    
    // Remove animation frame delay for faster LCP
    this.pageEntranceReady.value = true
    this.triggerCallbacks('onReadyForEntrance')
  }

  onProgress(callback) {
    this.callbacks.onProgress.push(callback)
  }

  onCriticalLoaded(callback) {
    this.callbacks.onCriticalLoaded.push(callback)
    if (this.criticalResourcesLoaded.value) {
      callback()
    }
  }

  onAllLoaded(callback) {
    this.callbacks.onAllLoaded.push(callback)
    if (this.assetsLoaded.value) {
      callback()
    }
  }

  onReadyForEntrance(callback) {
    this.callbacks.onReadyForEntrance.push(callback)
    if (this.pageEntranceReady.value) {
      callback()
    }
  }

  triggerCallbacks(type, data = null) {
    this.callbacks[type].forEach(callback => {
      try {
        callback(data)
      } catch (error) {
        console.error(`Loading orchestrator callback error (${type}):`, error)
      }
    })
  }

  // Computed properties for reactive state
  get canStartPageEntrance() {
    return computed(() => 
      this.criticalResourcesLoaded.value && 
      this.loadingComplete.value
    )
  }

  get isFullyReady() {
    return computed(() => 
      this.assetsLoaded.value && 
      this.pageEntranceReady.value
    )
  }

  // Reset method for development/testing
  reset() {
    this.isInitialized.value = false
    this.assetsLoaded.value = false
    this.criticalResourcesLoaded.value = false
    this.loadingProgress.value = 0
    this.loadingComplete.value = false
    this.pageEntranceReady.value = false
    this.loadedAssets.clear()
    
    Object.keys(this.callbacks).forEach(key => {
      this.callbacks[key] = []
    })
  }
}

// Create singleton instance
export const loadingOrchestrator = new LoadingOrchestrator()

// Provide convenient access to reactive state
export const useLoadingOrchestrator = () => {
  return {
    loadingOrchestrator,
    isInitialized: loadingOrchestrator.isInitialized,
    assetsLoaded: loadingOrchestrator.assetsLoaded,
    criticalResourcesLoaded: loadingOrchestrator.criticalResourcesLoaded,
    loadingProgress: loadingOrchestrator.loadingProgress,
    loadingComplete: loadingOrchestrator.loadingComplete,
    pageEntranceReady: loadingOrchestrator.pageEntranceReady,
    canStartPageEntrance: loadingOrchestrator.canStartPageEntrance,
    isFullyReady: loadingOrchestrator.isFullyReady
  }
}

export default loadingOrchestrator